import React, { useState, useEffect } from 'react';
import { useAuth } from '../../src/contexts/AuthContext';
import { api } from '../../src/services/api';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { User, Mail, Shield, Save, Loader2, Upload, Lock, Eye, EyeOff, Building2 } from 'lucide-react';

const CORP_EMAIL_DOMAIN = '@vigorenergy.com.br';

const maskCPF = (value: string) => value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');

const ProfileSettings = () => {
    const { profile, user, refreshProfile } = useAuth();
    const [fullName, setFullName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [emailCorporativo, setEmailCorporativo] = useState('');
    const [emailPessoal, setEmailPessoal] = useState('');
    const [cpf, setCpf] = useState('');
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Password change state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdMessage, setPwdMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        if (profile) {
            setFullName(profile.full_name || '');
            setJobTitle(profile.job_title || '');
            setAvatarUrl(profile.avatar_url || '');
            setEmailCorporativo(profile.email_corporativo || user?.email || '');
            setEmailPessoal(profile.email_pessoal || '');
            setCpf(profile.cpf || '');
        }
    }, [profile, user]);

    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setUploading(true);
            setMessage(null);

            if (!event.target.files || event.target.files.length === 0) {
                throw new Error('Selecione uma imagem para fazer upload.');
            }

            const file = event.target.files[0];
            const fileExt = file.name.split('.').pop();
            const fileName = `${profile?.id}/${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, file);

            if (uploadError) {
                throw uploadError;
            }

            const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
            setAvatarUrl(data.publicUrl);
            setMessage({ type: 'success', text: 'Imagem carregada! Clique em Salvar para confirmar.' });
        } catch (error: any) {
            console.error('Error uploading avatar:', error);
            setMessage({ type: 'error', text: error.message || 'Erro ao fazer upload da imagem.' });
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            if (!profile) return;

            const corp = emailCorporativo.trim();
            const currentLoginEmail = (user?.email || '').trim();
            const corpChanged = !!corp && corp.toLowerCase() !== currentLoginEmail.toLowerCase();

            // Regra de domínio para o e-mail corporativo (é o e-mail de login/assinatura do termo)
            if (corp && !corp.toLowerCase().endsWith(CORP_EMAIL_DOMAIN)) {
                setMessage({ type: 'error', text: `O e-mail corporativo deve terminar em ${CORP_EMAIL_DOMAIN}.` });
                setLoading(false);
                return;
            }

            // 1. Atualiza os campos do perfil (inclui o espelho do corporativo, o pessoal e o CPF)
            await api.auth.updateProfile(profile.id, {
                full_name: fullName,
                job_title: jobTitle,
                avatar_url: avatarUrl,
                email_corporativo: corp || null,
                email_pessoal: emailPessoal.trim() || null,
                cpf: cpf.trim() || null,
            });

            // 2. Se o corporativo mudou, atualiza o e-mail de login (auth) — dispara confirmação
            if (corpChanged) {
                const { error: authErr } = await supabase.auth.updateUser({ email: corp });
                if (authErr) throw authErr;
                setMessage({ type: 'success', text: 'Perfil atualizado. Enviamos um link de confirmação para o novo e-mail corporativo — confirme para concluir a troca do login.' });
            } else {
                setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
            }
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'perfil', acao: 'atualizar_perfil_proprio', origem: 'acao' });
            await refreshProfile();
        } catch (error: any) {
            console.error(error);
            setMessage({ type: 'error', text: error?.message || 'Erro ao atualizar perfil.' });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPwdMessage(null);

        if (newPassword.length < 6) {
            setPwdMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 6 caracteres.' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwdMessage({ type: 'error', text: 'As senhas não coincidem. Verifique e tente novamente.' });
            return;
        }

        setPwdLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;

            setPwdMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'perfil', acao: 'alterar_senha_propria', origem: 'acao' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            console.error('Error changing password:', error);
            setPwdMessage({ type: 'error', text: error.message || 'Erro ao alterar a senha.' });
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <>
            <div className="w-full bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm animate-fade-in transition-colors">
            <h2 className="text-xl font-bold font-poppins mb-6 dark:text-white">Suas Informações</h2>

            {message && (
                <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-6">
                <div className="flex items-start gap-6">
                    <img
                        src={avatarUrl || 'https://via.placeholder.com/100'}
                        alt="Avatar"
                        className="w-24 h-24 rounded-2xl object-cover border-2 border-gray-100 dark:border-gray-700"
                        onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100')}
                    />
                    <div className="flex-1 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Foto de Perfil</label>
                            <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm border border-gray-200 dark:border-gray-600 transition-colors">
                                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                <span>{uploading ? 'Enviando...' : 'Carregar nova foto'}</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarUpload}
                                    disabled={uploading}
                                    className="hidden"
                                />
                            </label>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">JPG, GIF ou PNG. Tam. máx. 5MB.</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome Completo</label>
                        <div className="relative">
                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cargo / Função</label>
                        <div className="relative">
                            <Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={jobTitle}
                                onChange={(e) => setJobTitle(e.target.value)}
                                placeholder="Ex: Diretor Comercial"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail Corporativo</label>
                        <div className="relative">
                            <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="email"
                                value={emailCorporativo}
                                onChange={(e) => setEmailCorporativo(e.target.value)}
                                placeholder={`nome${CORP_EMAIL_DOMAIN}`}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            É o seu e-mail de <strong>login</strong> e o usado para assinar termos. Deve terminar em <strong>{CORP_EMAIL_DOMAIN}</strong>. Ao alterá-lo, você receberá um link de confirmação no novo e-mail.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail Pessoal</label>
                        <div className="relative">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="email"
                                value={emailPessoal}
                                onChange={(e) => setEmailPessoal(e.target.value)}
                                placeholder="nome@gmail.com"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF</label>
                        <div className="relative">
                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={cpf}
                                onChange={(e) => setCpf(maskCPF(e.target.value))}
                                placeholder="000.000.000-00"
                                maxLength={14}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 mb-1 uppercase">Função (Role)</label>
                                <div className="text-sm font-medium text-gray-700 dark:text-white capitalize flex items-center gap-2">
                                    <Shield size={14} className="text-primary" />
                                    {profile?.role || '...'}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 mb-1 uppercase">Departamento</label>
                                <div className="flex flex-wrap gap-1.5 mt-0.5">
                                    {(profile?.departamentos && profile.departamentos.length > 0
                                        ? profile.departamentos
                                        : profile?.departamento ? [profile.departamento] : []
                                    ).map(d => (
                                        <span key={d} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary/90">{d}</span>
                                    ))}
                                    {(!profile?.departamentos || profile.departamentos.length === 0) && !profile?.departamento && (
                                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={loading || uploading}
                        className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-all disabled:opacity-70 shadow-lg shadow-primary/20"
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        Salvar Alterações
                    </button>
                </div>
            </form>
        </div>


        {/* Password Change Card */}
        <div className="w-full bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm mt-6 transition-colors">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                    <Lock size={20} />
                </div>
                <div>
                    <h2 className="text-xl font-bold font-poppins dark:text-white">Segurança</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Altere sua senha de acesso ao VigorHub.</p>
                </div>
            </div>

            {pwdMessage && (
                <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-medium ${pwdMessage.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {pwdMessage.text}
                </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* New password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nova Senha</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type={showNewPwd ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={6}
                                placeholder="Mínimo 6 caracteres"
                                className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            />
                            <button type="button" onClick={() => setShowNewPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Confirm password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmar Nova Senha</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type={showConfirmPwd ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                placeholder="Repita a nova senha"
                                className={`w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                                    confirmPassword && newPassword !== confirmPassword
                                        ? 'border-red-400 focus:ring-red-200 focus:border-red-400'
                                        : confirmPassword && newPassword === confirmPassword
                                        ? 'border-green-400 focus:ring-green-200 focus:border-green-400'
                                        : 'border-gray-200 dark:border-gray-700 focus:ring-primary/20 focus:border-primary'
                                }`}
                            />
                            <button type="button" onClick={() => setShowConfirmPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                {showConfirmPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        {confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-xs text-red-500 mt-1">As senhas não coincidem.</p>
                        )}
                        {confirmPassword && newPassword === confirmPassword && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">As senhas coincidem.</p>
                        )}
                    </div>
                </div>

                <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={pwdLoading || !newPassword || !confirmPassword}
                        className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-all disabled:opacity-70 shadow-lg shadow-primary/20"
                    >
                        {pwdLoading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />}
                        Alterar Senha
                    </button>
                </div>
            </form>
        </div>
        </>
    );
};

export default ProfileSettings;